import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do boutiques and retail stores get more foot traffic in Colorado?",
    a: "Local SEO + a tuned Google Business Profile + Google Shopping for ecommerce-enabled boutiques. 'Boutique near me', 'gift shop Colorado Springs', and 'women's clothing Denver' all convert when you rank locally and your storefront looks alive online.",
  },
  {
    q: "Should small boutiques sell online too?",
    a: "Yes — even if 80% of sales are in-store, ecommerce captures locals shopping after-hours and out-of-state visitors who shopped in person. We build hybrid sites with Shopify or WooCommerce that feel boutique, not big-box.",
  },
  {
    q: "Is Google Ads worth it for retail?",
    a: "Google Shopping for ecommerce yes — high-intent, low-CPC. Local search ads during Q4 (Nov–Dec) boost holiday foot traffic. Outside those, SEO and Instagram do the heavy lifting.",
  },
  {
    q: "What's the highest-ROI marketing for a small boutique?",
    a: "Instagram + a beautiful, fast site + email list + Google Business Profile. Boutiques are emotional purchases — visual brand, social proof, and reachability drive conversion.",
  },
  {
    q: "Do you build sites for multi-location retail?",
    a: "Yes — each location gets its own page with reviews, hours, and city-specific copy so each ranks in its own Map Pack. We handle storefront-only and ecommerce-blended retail.",
  },
];

export const Route = createFileRoute("/boutique-retail-marketing-colorado")({
  head: () =>
    industryMarketingHead("Boutique & Retail", "boutique or retail store", "boutique-retail-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Boutique & Retail"
      industryLower="boutique or retail store"
      slug="boutique-retail-marketing-colorado"
      subheadline="We help Colorado boutiques and independent retail shops drive in-store traffic and add an online sales channel that doesn't cannibalize the storefront."
      painPoints={[
        "Foot traffic dropped and Instagram alone isn't filling the gap",
        "Your site looks like a template and turns away buyers who'd shop in person",
        "Holiday season comes and goes without a measurable online sales lift",
      ]}
      serviceDescriptions={{
        seo: "Rank for boutique, gift shop, women's clothing, home goods, and every category — every Colorado market.",
        googleMaps: "Own the local Map Pack so 'boutique near me' and 'gift shop' searches walk in your door.",
        webDesign: "Boutique-feel ecommerce sites that look as good as your storefront and convert browsers into buyers.",
        ppc: "Google Shopping campaigns plus Q4 local ads that boost holiday foot traffic and online orders.",
        geoAi: "Get cited when buyers ask ChatGPT 'best boutique in Colorado Springs' or 'unique gift shop near me'.",
        socialMedia: "Instagram-to-site flows, email list growth, and retargeting that compounds repeat customers.",
      }}
      testimonial={{
        quote:
          "Foot traffic is up and we finally have an online channel that pays for itself. The site looks like our store, not a template.",
        role: "Boutique Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Boutique & retail marketing in Colorado Springs", description: "Drive in-store traffic across downtown, Old Colorado City, and Briargate retail districts." },
        { to: "/denver-digital-marketing", label: "Boutique & retail marketing in Denver", description: "Compete in Denver's saturated independent retail and boutique scene." },
        { to: "/boulder-digital-marketing", label: "Boutique & retail marketing in Boulder", description: "Premium and sustainable retail for Boulder and the Pearl Street corridor." },
      ]}
    />
  ),
});
